import React, { useState, useRef } from 'react';
import { Lock, AlertTriangle, Trash2 } from 'lucide-react';
import { ListingNotification } from '../services/StorageManager';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
import { cn } from '../utils/cn';
import { formatPrice as formatEuroAmount } from '../utils/money-utils';
import { Badge, Button, IconButton, buttonClasses } from './ui';
import { FactLine } from './listing/FactLine';

interface ListingCardProps {
    notification: ListingNotification;
    viewMode?: 'card' | 'list';
    onClick: (notification: ListingNotification) => void;
    onDelete: (notification: ListingNotification) => void;
}

/** A German PLZ embedded in the notification body (there is no dedicated `data.zip` field on the push payload). */
function extractZip(body: string): string | null {
    const match = body.match(/\b\d{5}\b/);
    return match ? match[0] : null;
}

function minutesAgo(timestamp: number): number {
    return Math.max(0, Math.floor((Date.now() - timestamp) / 60000));
}

/** "vor 3 Min." / "vor 2 Std." / "vor 1 Tag" — used both for the unread freshness badge and the muted read timestamp. */
function relativeTimeLabel(timestamp: number): string {
    const mins = minutesAgo(timestamp);
    if (mins < 1) return 'gerade jetzt';
    if (mins < 60) return `vor ${mins} Min.`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `vor ${hours} Std.`;
    const days = Math.floor(hours / 24);
    return `vor ${days} ${days === 1 ? 'Tag' : 'Tagen'}`;
}

export const ListingCard: React.FC<ListingCardProps> = ({ notification, viewMode = 'card', onClick, onDelete }) => {
    const { openUpgradeModal } = useUpgradeModal();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [swipeOffset, setSwipeOffset] = useState(0);
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);
    const isSwiping = useRef(false);
    const cardRef = useRef<HTMLDivElement>(null);

    // Metadata
    const data = notification.data || {};
    const isScrambled = !!data.isScrambled;
    const isMaintenance = data.type === 'maintenance' || data.type === 'force-reload';

    // Image handling
    const image = data.imageUrl || data.image || data.icon;
    const hasImage = !!image && !image.includes('logo');

    const detailedRent = data.detailedRent;
    const rentRaw = detailedRent?.raw; // For scrambled listings showing a range like "800–900 €"
    const rentValue: number | null = data.rent ?? detailedRent?.base ?? null;
    const rentKind: 'warm' | 'kalt' | undefined = data.rent != null ? 'warm' : detailedRent?.base != null ? 'kalt' : undefined;
    const platform = data.platform || data.scraperName || null;
    const zip = extractZip(notification.body);

    // Rent breakdown ("Kalt 890 € + NK 210 €") and the estimate marker — same
    // source fields/conditions as the pre-redesign card.
    const baseRentFormatted = detailedRent?.base != null ? formatEuroAmount(detailedRent.base) : null;
    const utilitiesFormatted = detailedRent?.utilities != null ? formatEuroAmount(detailedRent.utilities) : null;
    const isEstimated = !!(detailedRent?.totalEstimated || detailedRent?.baseEstimated || detailedRent?.utilitiesEstimated);
    const rentBreakdownParts = [
        baseRentFormatted && `Kalt ${baseRentFormatted}`,
        utilitiesFormatted && `NK ${utilitiesFormatted}`,
    ].filter(Boolean) as string[];

    // Category flags (WBS/WG/Tausch/Befristet/Privat) — restored as neutral badge pills.
    const categoryFlags: Array<{ key: string; label: string }> = [
        data.isWbs && { key: 'wbs', label: 'WBS' },
        data.isWg && { key: 'wg', label: 'WG' },
        data.isSwap && { key: 'swap', label: 'Tausch' },
        data.isTemp && { key: 'temp', label: 'Befristet' },
        data.isPrivate && { key: 'private', label: 'Privat' },
    ].filter(Boolean) as Array<{ key: string; label: string }>;

    // Two distinct "view the ad" actions when a listing has both (today's Exposé/Bewerben
    // case): listingUrl stays primary "Anzeige öffnen", applyUrl becomes secondary "Bewerben".
    // With only one of the two, it alone renders as the single primary "Anzeige öffnen".
    const listingUrl = data.listingUrl || null;
    const applyUrl = data.applyUrl || null;
    const mapsUrl = data.mapsUrl || null;

    const timeLabel = relativeTimeLabel(notification.timestamp);
    const freshnessLabel = `neu, ${timeLabel}`;

    // Touch handlers for swipe-to-delete
    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
        isSwiping.current = false;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        const deltaX = e.touches[0].clientX - touchStartX.current;
        const deltaY = e.touches[0].clientY - touchStartY.current;

        // Only swipe if horizontal movement is dominant
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
            isSwiping.current = true;
            // Only allow left swipe (negative deltaX)
            if (deltaX < 0) {
                setSwipeOffset(Math.max(deltaX, -100));
            }
        }
    };

    const handleTouchEnd = () => {
        if (swipeOffset < -60) {
            // Swipe threshold reached, show delete confirmation
            setShowDeleteConfirm(true);
        }
        setSwipeOffset(0);
        isSwiping.current = false;
    };

    const handleCardClick = (e: React.MouseEvent | React.KeyboardEvent) => {
        if (isSwiping.current || showDeleteConfirm) {
            e.stopPropagation();
            return;
        }
        if (isScrambled) {
            openUpgradeModal('pro');
            return;
        }
        if (notification.url && !isMaintenance) {
            onClick(notification);
        }
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDeleteConfirm(true);
    };

    const confirmDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete(notification);
    };

    const cancelDelete = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation();
        setShowDeleteConfirm(false);
    };

    const handleConfirmKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === 'Return') {
            onDelete(notification);
        } else if (e.key === 'Escape') {
            setShowDeleteConfirm(false);
        }
    };

    const deleteConfirmOverlay = (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-scrim/60 p-4" onClick={cancelDelete}>
            <div
                className="w-full max-w-xs rounded-card bg-card p-4 text-center shadow-card"
                onClick={(e) => e.stopPropagation()}
            >
                <p className="text-sm font-medium text-ink">Diesen Treffer löschen?</p>
                <div className="mt-3 flex justify-center gap-2">
                    <Button type="button" variant="secondary" size="sm" onClick={cancelDelete}>
                        Abbrechen
                    </Button>
                    <Button type="button" variant="danger" size="sm" autoFocus onClick={confirmDelete} onKeyDown={handleConfirmKeyDown}>
                        Löschen
                    </Button>
                </div>
            </div>
        </div>
    );

    const maintenanceIcon = <AlertTriangle className="h-5 w-5 shrink-0 text-warning-text" aria-hidden="true" />;

    const facts = { rent: rentValue ?? undefined, rentKind, rooms: data.rooms, area: data.space };
    const hasFacts = facts.rent != null || facts.rooms != null || facts.area != null;

    const rentBreakdownBlock =
        !isScrambled && (rentBreakdownParts.length > 0 || isEstimated) ? (
            <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-muted">
                {rentBreakdownParts.length > 0 && <span className="tabular">{rentBreakdownParts.join(' + ')}</span>}
                {isEstimated && <Badge tone="neutral">geschätzt</Badge>}
            </p>
        ) : null;

    const categoryFlagsBlock =
        !isMaintenance && !isScrambled && categoryFlags.length > 0 ? (
            <div className="mt-1 flex flex-wrap gap-1.5">
                {categoryFlags.map((flag) => (
                    <Badge key={flag.key} tone="neutral">
                        {flag.label}
                    </Badge>
                ))}
            </div>
        ) : null;

    if (viewMode === 'list') {
        return (
            <>
                <div
                    ref={cardRef}
                    role={notification.url && !isMaintenance ? 'button' : 'presentation'}
                    tabIndex={notification.url && !isMaintenance ? 0 : -1}
                    className={cn(
                        'listing-row list-view group relative flex items-center gap-3 rounded-control border border-line bg-card px-3 py-2 transition-transform',
                        notification.read ? 'read' : 'unread border-l-4 border-signal',
                        isMaintenance && 'maintenance',
                        showDeleteConfirm && 'delete-confirm'
                    )}
                    style={{ transform: `translateX(${swipeOffset}px)`, cursor: notification.url && !isMaintenance ? 'pointer' : 'default' }}
                    onClick={handleCardClick}
                    onKeyDown={(e) => {
                        if (notification.url && (e.key === 'Enter' || e.key === ' ')) handleCardClick(e);
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    {isMaintenance && maintenanceIcon}
                    <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                            <span className="truncate text-sm font-medium text-ink">{notification.title}</span>
                            <span className="shrink-0 text-xs text-muted">{timeLabel}</span>
                        </div>
                        {isMaintenance ? (
                            <p className="truncate text-xs text-muted">{notification.body}</p>
                        ) : isScrambled ? (
                            <p className="mt-0.5 flex items-center gap-1 text-sm font-medium text-ink">
                                <Lock className="h-3.5 w-3.5 text-muted" aria-hidden="true" />
                                {rentRaw}
                            </p>
                        ) : (
                            <FactLine facts={facts} size="sm" className="mt-0.5" />
                        )}
                        {rentBreakdownBlock}
                        {categoryFlagsBlock}
                    </div>
                    {!notification.read && <span className="unread-indicator h-2 w-2 shrink-0 rounded-full bg-signal" aria-hidden="true" />}
                    <IconButton
                        label="Treffer löschen"
                        icon={<Trash2 className="h-4 w-4" />}
                        variant="ghost"
                        size="sm"
                        onClick={handleDeleteClick}
                    />
                </div>
                {showDeleteConfirm && deleteConfirmOverlay}
            </>
        );
    }

    return (
        <>
            <div
                ref={cardRef}
                role={notification.url && !isMaintenance ? 'button' : 'presentation'}
                tabIndex={notification.url && !isMaintenance ? 0 : -1}
                data-testid="listing-card-body"
                className={cn(
                    'group relative flex flex-col gap-3 overflow-hidden rounded-[18px] bg-card p-4 shadow-card transition-transform sm:flex-row sm:gap-4',
                    notification.read ? 'read' : 'unread border-l-4 border-signal',
                    isMaintenance && 'maintenance',
                    showDeleteConfirm && 'delete-confirm'
                )}
                style={{ transform: `translateX(${swipeOffset}px)`, cursor: notification.url && !isMaintenance ? 'pointer' : 'default' }}
                onClick={handleCardClick}
                onKeyDown={(e) => {
                    if (notification.url && !isMaintenance && (e.key === 'Enter' || e.key === ' ')) handleCardClick(e);
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {isMaintenance ? (
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[10px] bg-warning-surface sm:h-20 sm:w-20">
                        {maintenanceIcon}
                    </div>
                ) : isScrambled ? (
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[10px] bg-sunken text-muted sm:h-20 sm:w-20">
                        <Lock className="h-6 w-6" aria-hidden="true" />
                    </div>
                ) : hasImage ? (
                    <div className="h-40 w-full shrink-0 overflow-hidden rounded-[10px] sm:h-20 sm:w-28">
                        <img src={image} alt="" className="h-full w-full object-cover" />
                    </div>
                ) : null}

                <div className="min-w-0 flex-1 pr-8">
                    <div className="flex items-start justify-between gap-2">
                        <h3 className="line-clamp-2 flex items-start gap-1.5 font-display text-base font-semibold text-ink">
                            {!notification.read && (
                                <span className="unread-indicator mt-1.5 h-2 w-2 shrink-0 rounded-full bg-signal" aria-hidden="true" />
                            )}
                            {notification.title}
                        </h3>
                        {!notification.read ? (
                            <Badge tone="signal" className="shrink-0">
                                {freshnessLabel}
                            </Badge>
                        ) : (
                            <span className="shrink-0 text-xs text-muted">{timeLabel}</span>
                        )}
                    </div>

                    {isMaintenance ? (
                        <p className="mt-1 text-sm text-muted">{notification.body}</p>
                    ) : isScrambled ? (
                        <p className="mt-1 flex items-center gap-1 text-sm font-medium text-ink">
                            <Lock className="h-4 w-4 text-muted" aria-hidden="true" />
                            {rentRaw}
                        </p>
                    ) : hasFacts ? (
                        <FactLine facts={facts} size="sm" className="mt-1" />
                    ) : (
                        // Fallback for notifications without structured rent/rooms/area data
                        // (e.g. legacy or test messages) — the free-text body.
                        <p className="mt-1 text-sm text-muted">{notification.body}</p>
                    )}
                    {rentBreakdownBlock}
                    {categoryFlagsBlock}

                    {(zip || platform) && (
                        <p className="tabular mt-1 text-sm text-muted">
                            {zip && <span>{zip}</span>}
                            {platform && <span className={cn(zip && 'border-l border-line-strong/60 pl-2 ml-2')}>{platform}</span>}
                        </p>
                    )}

                    {isScrambled ? (
                        <p className="mt-2 text-sm text-muted">Details ab Pro sichtbar.</p>
                    ) : !isMaintenance && (listingUrl || applyUrl || mapsUrl) ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {listingUrl && (
                                <a
                                    href={listingUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={buttonClasses('primary', 'sm')}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Anzeige öffnen
                                </a>
                            )}
                            {applyUrl && (
                                <a
                                    href={applyUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={buttonClasses(listingUrl ? 'secondary' : 'primary', 'sm')}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {listingUrl ? 'Bewerben' : 'Anzeige öffnen'}
                                </a>
                            )}
                            {mapsUrl && (
                                <a
                                    href={mapsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={buttonClasses('secondary', 'sm')}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Karte
                                </a>
                            )}
                        </div>
                    ) : null}
                </div>

                <IconButton
                    label="Treffer löschen"
                    icon={<Trash2 className="h-4 w-4" />}
                    variant="ghost"
                    size="sm"
                    onClick={handleDeleteClick}
                    className="absolute right-2 top-2 sm:right-3 sm:top-3"
                />
            </div>

            {showDeleteConfirm && deleteConfirmOverlay}
        </>
    );
};
